import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import dico from "../../../util/src/language/dico";
import BoxShadowPicker from "./BoxShadowPicker";
import { EDITOR_PICKER_POPOVER_Z_INDEX } from "./editorPickerPopoverZIndex";

type FilterValues = {
  blur: number;
  brightness: number;
  contrast: number;
  grayscale: number;
  hueRotate: number;
  invert: number;
  opacity: number;
  saturate: number;
  sepia: number;
  dropShadow: string;
  svgFileName: string;
  svgFilterId: string;
};

export type FilterPickerProps = {
  value: string;
  disabled?: boolean;
  onChange: (nextValue: string) => void;
  onSelectSvgFile?: () => Promise<string | null>;
};

const DEFAULT_VALUES: FilterValues = {
  blur: 0,
  brightness: 1,
  contrast: 100,
  grayscale: 0,
  hueRotate: 0,
  invert: 0,
  opacity: 100,
  saturate: 100,
  sepia: 0,
  dropShadow: "",
  svgFileName: "",
  svgFilterId: "",
};

const stripQuotes = (value: string): string =>
  value.replace(/^['"]|['"]$/g, "").trim();

const UUID_PREFIXED_FILE_NAME_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-(.+)$/i;

const toDisplaySvgFileName = (rawFileName: string): string => {
  const trimmed = rawFileName.trim();
  if (!trimmed) return "";
  const match = trimmed.match(UUID_PREFIXED_FILE_NAME_REGEX);
  return match?.[1] ?? trimmed;
};

const parseSvgUrlParts = (
  rawUrlExpression: string,
): Pick<FilterValues, "svgFileName" | "svgFilterId"> => {
  const rawInner = rawUrlExpression
    .trim()
    .replace(/^url\((.*)\)$/i, "$1")
    .trim();
  const unquoted = stripQuotes(rawInner);
  if (!unquoted) {
    return { svgFileName: "", svgFilterId: "" };
  }
  const [rawPath = "", rawHash] = unquoted.split("#", 2);
  const fileName = rawPath.split("/").filter(Boolean).pop() ?? "";
  const filterId = rawHash?.trim() ?? "";
  return { svgFileName: fileName, svgFilterId: filterId };
};

const buildSvgUrlExpression = (
  fileName: string,
  filterId: string,
): string | undefined => {
  const safeFileName = fileName.trim();
  if (!safeFileName) return undefined;
  const hash = filterId.trim() ? `#${filterId.trim()}` : "";
  return `url("/user-layouts/images/${safeFileName}${hash}")`;
};

const parseFilter = (value: string): FilterValues => {
  const result = { ...DEFAULT_VALUES };
  const trimmed = value.trim();
  if (!trimmed || trimmed === "none") return result;

  const blurMatch = trimmed.match(/blur\(([0-9]*\.?[0-9]+)px\)/);
  if (blurMatch?.[1]) result.blur = Number.parseFloat(blurMatch[1]);

  const brightnessMatch = trimmed.match(/brightness\(([0-9]*\.?[0-9]+)\)/);
  if (brightnessMatch?.[1])
    result.brightness = Number.parseFloat(brightnessMatch[1]);

  const contrastMatch = trimmed.match(/contrast\(([0-9]*\.?[0-9]+)%?\)/);
  if (contrastMatch?.[1]) result.contrast = Number.parseFloat(contrastMatch[1]);

  const grayscaleMatch = trimmed.match(/grayscale\(([0-9]*\.?[0-9]+)%?\)/);
  if (grayscaleMatch?.[1])
    result.grayscale = Number.parseFloat(grayscaleMatch[1]);

  const hueMatch = trimmed.match(/hue-rotate\((-?[0-9]*\.?[0-9]+)deg\)/);
  if (hueMatch?.[1]) result.hueRotate = Number.parseFloat(hueMatch[1]);

  const invertMatch = trimmed.match(/invert\(([0-9]*\.?[0-9]+)%?\)/);
  if (invertMatch?.[1]) result.invert = Number.parseFloat(invertMatch[1]);

  const opacityMatch = trimmed.match(/opacity\(([0-9]*\.?[0-9]+)%?\)/);
  if (opacityMatch?.[1]) result.opacity = Number.parseFloat(opacityMatch[1]);

  const saturateMatch = trimmed.match(/saturate\(([0-9]*\.?[0-9]+)%?\)/);
  if (saturateMatch?.[1]) result.saturate = Number.parseFloat(saturateMatch[1]);

  const sepiaMatch = trimmed.match(/sepia\(([0-9]*\.?[0-9]+)%?\)/);
  if (sepiaMatch?.[1]) result.sepia = Number.parseFloat(sepiaMatch[1]);

  const dropShadowMatch = trimmed.match(/drop-shadow\(([^)]+)\)/);
  if (dropShadowMatch?.[1]) result.dropShadow = dropShadowMatch[1].trim();

  const urlMatch = trimmed.match(/url\(([^)]+)\)/);
  if (urlMatch?.[0]) {
    const parsed = parseSvgUrlParts(urlMatch[0]);
    result.svgFileName = parsed.svgFileName;
    result.svgFilterId = parsed.svgFilterId;
  }

  return result;
};

const stringifyFilter = (values: FilterValues): string => {
  const parts: string[] = [];

  const svgUrlExpression = buildSvgUrlExpression(
    values.svgFileName,
    values.svgFilterId,
  );
  if (svgUrlExpression) parts.push(svgUrlExpression);
  if (values.blur !== 0) parts.push(`blur(${values.blur}px)`);
  if (values.brightness !== 1) parts.push(`brightness(${values.brightness})`);
  if (values.contrast !== 100) parts.push(`contrast(${values.contrast}%)`);
  if (values.grayscale !== 0) parts.push(`grayscale(${values.grayscale}%)`);
  if (values.hueRotate !== 0) parts.push(`hue-rotate(${values.hueRotate}deg)`);
  if (values.invert !== 0) parts.push(`invert(${values.invert}%)`);
  if (values.opacity !== 100) parts.push(`opacity(${values.opacity}%)`);
  if (values.saturate !== 100) parts.push(`saturate(${values.saturate}%)`);
  if (values.sepia !== 0) parts.push(`sepia(${values.sepia}%)`);
  if (values.dropShadow) parts.push(`drop-shadow(${values.dropShadow})`);

  return parts.length > 0 ? parts.join(" ") : "none";
};

/**
 * Edits a CSS `filter` property value with individual controls for each
 * filter function (blur, brightness, contrast, etc.), an SVG URL selector
 * via callback, and drop-shadow editing via BoxShadowPicker (without inset).
 *
 * @param value - Current CSS filter string.
 * @param disabled - Disables interaction.
 * @param onChange - Callback with the updated CSS string.
 * @param onSelectSvgFile - Async callback to select/upload an SVG file, returning the stored file name or null.
 */
const FilterPicker: React.FC<FilterPickerProps> = ({
  value,
  disabled = false,
  onChange,
  onSelectSvgFile,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popupOpen = Boolean(anchorEl);

  const labels =
    (
      dico().licensedPortal?.layoutEditor as {
        filterPicker?: Record<string, string>;
      }
    )?.filterPicker ?? ({} as Record<string, string>);

  const filterValues = useMemo(() => parseFilter(value), [value]);

  const emitChange = useCallback(
    (partial: Partial<FilterValues>) => {
      const next = { ...filterValues, ...partial };
      onChange(stringifyFilter(next));
    },
    [filterValues, onChange],
  );

  const handleSvgSelect = useCallback(async () => {
    if (!onSelectSvgFile) return;
    const result = await onSelectSvgFile();
    if (result !== null) {
      emitChange({ svgFileName: result });
    }
  }, [onSelectSvgFile, emitChange]);

  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={(e) => {
          if (!disabled) setAnchorEl(e.currentTarget as HTMLElement);
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setAnchorEl(e.currentTarget as HTMLElement);
          }
        }}
        sx={{
          border: "1px solid #d8e0eb",
          borderRadius: "8px",
          backgroundColor: "#fbfcfe",
          p: "3px 8px",
          height: 40,
          display: "flex",
          alignItems: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          "&:hover": {
            borderColor: disabled ? "#d8e0eb" : "#94a3b8",
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: 30,
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            background:
              "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 45%, #cbd5e1 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            filter: value && value !== "none" ? value : undefined,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: "#334155",
              userSelect: "none",
            }}
          >
            IMG
          </Typography>
        </Box>
      </Box>

      <Popover
        open={popupOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
            sx: { zIndex: EDITOR_PICKER_POPOVER_Z_INDEX },
          },
        }}
      >
        <Box
          sx={{
            width: 360,
            maxHeight: "70vh",
            overflow: "auto",
            background: "#fff",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}
            >
              {labels.title ?? "Filter"}
            </Typography>
            <Tooltip title={labels.reset ?? ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange("none")}
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <FilterField
              label={labels.blur ?? "Blur"}
              unit="px"
              value={filterValues.blur}
              min={0}
              step={1}
              onChange={(v) => emitChange({ blur: v })}
            />
            <FilterField
              label={labels.brightness ?? "Brightness"}
              value={filterValues.brightness}
              min={0}
              step={0.05}
              onChange={(v) => emitChange({ brightness: v })}
            />
            <FilterField
              label={labels.contrast ?? "Contrast"}
              unit="%"
              value={filterValues.contrast}
              min={0}
              step={1}
              onChange={(v) => emitChange({ contrast: v })}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 1,
              mb: 1,
            }}
          >
            <FilterField
              label={labels.grayscale ?? "Grayscale"}
              unit="%"
              value={filterValues.grayscale}
              min={0}
              max={100}
              step={1}
              onChange={(v) => emitChange({ grayscale: v })}
            />
            <FilterField
              label={labels.hueRotate ?? "Hue rotate"}
              unit="deg"
              value={filterValues.hueRotate}
              step={1}
              onChange={(v) => emitChange({ hueRotate: v })}
            />
            <FilterField
              label={labels.invert ?? "Invert"}
              unit="%"
              value={filterValues.invert}
              min={0}
              max={100}
              step={1}
              onChange={(v) => emitChange({ invert: v })}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 1,
              mb: 1.5,
            }}
          >
            <FilterField
              label={labels.opacity ?? "Opacity"}
              unit="%"
              value={filterValues.opacity}
              min={0}
              max={100}
              step={1}
              onChange={(v) => emitChange({ opacity: v })}
            />
            <FilterField
              label={labels.saturate ?? "Saturate"}
              unit="%"
              value={filterValues.saturate}
              min={0}
              step={1}
              onChange={(v) => emitChange({ saturate: v })}
            />
            <FilterField
              label={labels.sepia ?? "Sepia"}
              unit="%"
              value={filterValues.sepia}
              min={0}
              max={100}
              step={1}
              onChange={(v) => emitChange({ sepia: v })}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={sectionLabelSx}>
              {labels.dropShadow ?? "Drop shadow"}
            </Typography>
            <BoxShadowPicker
              value={filterValues.dropShadow}
              disabled={disabled}
              hideInset
              onChange={(next) => emitChange({ dropShadow: next })}
            />
          </Box>

          <Box>
            <Typography sx={sectionLabelSx}>
              {labels.svgUrl ?? "SVG filter URL"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                fullWidth
                value={toDisplaySvgFileName(filterValues.svgFileName)}
                disabled
                placeholder={labels.svgFileNamePlaceholder ?? "file.svg"}
                sx={inputSx}
              />
              <TextField
                size="small"
                fullWidth
                value={filterValues.svgFilterId}
                placeholder={
                  labels.svgFilterIdPlaceholder ?? "filter-element-id"
                }
                onChange={(e) => emitChange({ svgFilterId: e.target.value })}
                sx={inputSx}
              />
              {onSelectSvgFile && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  onClick={handleSvgSelect}
                  sx={{
                    minWidth: "auto",
                    px: 1.5,
                    height: 36,
                    textTransform: "none",
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  {labels.selectFile ?? "..."}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

type FilterFieldProps = {
  label: string;
  unit?: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
};

const FilterField: React.FC<FilterFieldProps> = ({
  label,
  unit,
  value,
  min,
  max,
  step = 1,
  onChange,
}) => (
  <Box>
    <Typography sx={sectionLabelSx}>
      {label}
      {unit ? ` (${unit})` : ""}
    </Typography>
    <TextField
      size="small"
      type="number"
      fullWidth
      value={value}
      onChange={(e) => {
        let v = Number(e.target.value) || 0;
        if (min !== undefined) v = Math.max(min, v);
        if (max !== undefined) v = Math.min(max, v);
        onChange(v);
      }}
      sx={inputSx}
      slotProps={{ htmlInput: { min, max, step } }}
    />
  </Box>
);

const sectionLabelSx = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  mb: 0.75,
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 36,
    borderRadius: "6px",
    "& input": { fontSize: 13 },
  },
};

export default FilterPicker;
